import { useState, useEffect, useRef } from 'react'
import { sanitizeHtml } from '../utils/sanitizeHtml'
import { loadNotes, createNote, updateNote, deleteNote } from '../lib/apiPhpAdapter'
import type { Note, NoteColorTag } from '../types'
import './NotesPanel.css'

const COLOR_TAGS: { value: NoteColorTag; label: string; color: string }[] = [
  { value: null, label: 'Bez barvy', color: '#e0e0e0' },
  { value: 'red', label: 'Červená', color: '#ff6b6b' },
  { value: 'blue', label: 'Modrá', color: '#4dabf7' },
  { value: 'green', label: 'Zelená', color: '#51cf66' },
  { value: 'yellow', label: 'Žlutá', color: '#ffd43b' },
  { value: 'purple', label: 'Fialová', color: '#9775fa' },
  { value: 'orange', label: 'Oranžová', color: '#ff922b' },
  { value: 'pink', label: 'Růžová', color: '#f783ac' },
  { value: 'gray', label: 'Šedá', color: '#adb5bd' },
]

export default function NotesPanel() {
  const [notes, setNotes] = useState<Note[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [orderBy, setOrderBy] = useState<'created_at' | 'updated_at' | 'title'>('created_at')
  const [orderDir, setOrderDir] = useState<'ASC' | 'DESC'>('DESC')
  const [selectedNote, setSelectedNote] = useState<Note | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editColorTag, setEditColorTag] = useState<NoteColorTag>(null)
  const [editIsPinned, setEditIsPinned] = useState(false)
  const contentEditableRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    loadNotesData()
  }, [searchQuery, orderBy, orderDir])

  const loadNotesData = async () => {
    try {
      setLoading(true)
      const notesData = await loadNotes(searchQuery || undefined, orderBy, orderDir)
      setNotes(notesData)
    } catch (error: any) {
      console.error('Error loading notes:', error)
      const errorMessage = error?.message || 'Chyba při načítání poznámek'
      if (errorMessage.includes('Database') || errorMessage.includes('Table') || errorMessage.includes('doesn\'t exist')) {
        alert('Tabulka poznámek ještě nebyla vytvořena. Prosím, kontaktujte administrátora.')
      } else {
        alert('Chyba při načítání poznámek: ' + errorMessage)
      }
      setNotes([]) // Устанавливаем пустой массив, чтобы не было белого экрана
    } finally {
      setLoading(false)
    }
  }

  const handleCreateNote = () => {
    setSelectedNote(null)
    setEditTitle('')
    setEditColorTag(null)
    setEditIsPinned(false)
    setIsEditing(true)
    if (contentEditableRef.current) {
      contentEditableRef.current.innerHTML = ''
    }
  }

  const handleEditNote = (note: Note) => {
    setSelectedNote(note)
    setEditTitle(note.title)
    setEditColorTag(note.color_tag || null)
    setEditIsPinned(note.is_pinned || false)
    setIsEditing(true)
    // Устанавливаем содержимое после рендера
    setTimeout(() => {
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = note.content
      }
    }, 0)
  }

  const handleCancelEdit = () => {
    setIsEditing(false)
    setSelectedNote(null)
    setEditTitle('')
    setEditColorTag(null)
    setEditIsPinned(false)
    if (contentEditableRef.current) {
      contentEditableRef.current.innerHTML = ''
    }
  }

  const handleSaveNote = async () => {
    if (!editTitle.trim()) {
      alert('Zadejte název poznámky')
      return
    }

    const content = contentEditableRef.current?.innerHTML || ''
    const textContent = contentEditableRef.current?.innerText || ''
    
    if (textContent.length > 3000) {
      alert('Obsah poznámky je příliš dlouhý (max 3000 znaků)')
      return
    }

    try {
      setSaving(true)
      
      if (selectedNote) {
        // Update existing note
        await updateNote(selectedNote.id, {
          title: editTitle.trim(),
          content,
          color_tag: editColorTag,
          is_pinned: editIsPinned,
        })
      } else {
        // Create new note
        await createNote({
          title: editTitle.trim(),
          content,
          color_tag: editColorTag,
          is_pinned: editIsPinned,
        })
      }

      await loadNotesData()
      handleCancelEdit()
      alert('Poznámka byla úspěšně uložena')
    } catch (error: any) {
      console.error('Error saving note:', error)
      const errorMessage = error?.message || 'Chyba při ukládání poznámky'
      if (errorMessage.includes('Database') || errorMessage.includes('Table') || errorMessage.includes('doesn\'t exist')) {
        alert('Tabulka poznámek ještě nebyla vytvořena. Prosím, kontaktujte administrátora nebo vytvořte tabulku pomocí SQL skriptu.')
      } else {
        alert('Chyba při ukládání poznámky: ' + errorMessage)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteNote = async (noteId: string) => {
    if (!confirm('Opravdu chcete smazat tuto poznámku?')) return

    try {
      await deleteNote(noteId)
      await loadNotesData()
      if (selectedNote?.id === noteId) {
        handleCancelEdit()
      }
      alert('Poznámka byla smazána')
    } catch (error: any) {
      console.error('Error deleting note:', error)
      const errorMessage = error?.message || 'Chyba při mazání poznámky'
      alert('Chyba při mazání poznámky: ' + errorMessage)
    }
  }

  const togglePin = async (note: Note) => {
    try {
      await updateNote(note.id, {
        title: note.title,
        content: note.content,
        color_tag: note.color_tag || null,
        is_pinned: !note.is_pinned,
      })
      await loadNotesData()
      if (selectedNote?.id === note.id) {
        setEditIsPinned(!note.is_pinned)
      }
    } catch (error: any) {
      console.error('Error toggling pin:', error)
      const errorMessage = error?.message || 'Chyba při změně připnutí'
      alert('Chyba při změně připnutí: ' + errorMessage)
    }
  }

  // Rich text editor commands
  const execCommand = (command: string, value: string | null = null) => {
    document.execCommand(command, false, value || undefined)
    if (contentEditableRef.current) {
      contentEditableRef.current.focus()
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getColorTagStyle = (colorTag: NoteColorTag) => {
    if (!colorTag) return {}
    const tag = COLOR_TAGS.find(t => t.value === colorTag)
    return tag ? { borderLeft: `4px solid ${tag.color}` } : {}
  }

  const getColorTagClass = (colorTag: NoteColorTag) => {
    if (!colorTag) return ''
    return `note-color-${colorTag}`
  }

  if (loading) {
    return <div className="notes-panel-loading">Načítání poznámek...</div>
  }

  return (
    <div className="notes-panel">
      <div className="notes-header">
        <h2>📝 Moje poznámky</h2>
        <button onClick={handleCreateNote} className="create-note-button">
          ➕ Nová poznámka
        </button>
      </div>

      {isEditing ? (
        <div className="note-editor">
          <div className="editor-header">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Název poznámky"
              className="editor-title-input"
            />
            <div className="editor-actions-top">
              <label className="pin-checkbox">
                <input
                  type="checkbox"
                  checked={editIsPinned}
                  onChange={(e) => setEditIsPinned(e.target.checked)}
                />
                📌 Připnout
              </label>
              <select
                value={editColorTag || ''}
                onChange={(e) => setEditColorTag(e.target.value as NoteColorTag || null)}
                className="color-tag-select"
              >
                {COLOR_TAGS.map(tag => (
                  <option key={tag.value || 'null'} value={tag.value || ''}>
                    {tag.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="rich-text-toolbar">
            <button onClick={() => execCommand('bold')} title="Tučné" className="toolbar-button">
              <strong>B</strong>
            </button>
            <button onClick={() => execCommand('italic')} title="Kurzíva" className="toolbar-button">
              <em>I</em>
            </button>
            <button onClick={() => execCommand('underline')} title="Podtržení" className="toolbar-button">
              <u>U</u>
            </button>
            <div className="toolbar-separator" />
            <button onClick={() => execCommand('foreColor', '#ff0000')} title="Červená" className="toolbar-button color-red">
              🔴
            </button>
            <button onClick={() => execCommand('foreColor', '#0000ff')} title="Modrá" className="toolbar-button color-blue">
              🔵
            </button>
            <button onClick={() => execCommand('foreColor', '#008000')} title="Zelená" className="toolbar-button color-green">
              🟢
            </button>
            <button onClick={() => execCommand('foreColor', '#ffff00')} title="Žlutá" className="toolbar-button color-yellow">
              🟡
            </button>
            <button onClick={() => execCommand('foreColor', '#000000')} title="Černá" className="toolbar-button">
              ⚫
            </button>
            <div className="toolbar-separator" />
            <button onClick={() => execCommand('formatBlock', 'h3')} title="Nadpis" className="toolbar-button">
              H
            </button>
            <button onClick={() => execCommand('insertUnorderedList')} title="Seznam" className="toolbar-button">
              •
            </button>
            <button onClick={() => execCommand('removeFormat')} title="Odstranit formátování" className="toolbar-button">
              🧹
            </button>
          </div>

          <div
            ref={contentEditableRef}
            contentEditable
            className="rich-text-editor"
            style={{ minHeight: '200px', maxHeight: '400px', overflowY: 'auto' }}
            onInput={(e) => {
              const textContent = e.currentTarget.innerText || ''
              if (textContent.length > 3000) {
                alert('Maximální délka je 3000 znaků')
              }
            }}
          />

          <div className="editor-actions">
            <button onClick={handleCancelEdit} className="cancel-button">
              Zrušit
            </button>
            <button onClick={handleSaveNote} disabled={saving} className="save-button">
              {saving ? 'Ukládání...' : '💾 Uložit'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="notes-controls">
            <input
              type="text"
              placeholder="🔍 Hledat poznámky..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            <select
              value={orderBy}
              onChange={(e) => setOrderBy(e.target.value as 'created_at' | 'updated_at' | 'title')}
              className="order-select"
            >
              <option value="created_at">Datum vytvoření</option>
              <option value="updated_at">Datum úpravy</option>
              <option value="title">Název</option>
            </select>
            <button
              onClick={() => setOrderDir(orderDir === 'ASC' ? 'DESC' : 'ASC')}
              className="order-dir-button"
              title={orderDir === 'ASC' ? 'Vzestupně' : 'Sestupně'}
            >
              {orderDir === 'ASC' ? '↑' : '↓'}
            </button>
          </div>

          <div className="notes-list">
            {notes.length === 0 ? (
              <div className="notes-empty">
                {searchQuery ? 'Žádné poznámky neodpovídají hledání' : 'Nemáte žádné poznámky. Vytvořte novou!'}
              </div>
            ) : (
              notes.map(note => (
                <div
                  key={note.id}
                  className={`note-card ${getColorTagClass(note.color_tag || null)} ${note.is_pinned ? 'pinned' : ''}`}
                  style={getColorTagStyle(note.color_tag || null)}
                  onClick={() => handleEditNote(note)}
                >
                  <div className="note-card-header">
                    <h3 className="note-title">{note.title}</h3>
                    <div className="note-actions" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => togglePin(note)}
                        className={`pin-button ${note.is_pinned ? 'pinned' : ''}`}
                        title={note.is_pinned ? 'Odepnout' : 'Připnout'}
                      >
                        {note.is_pinned ? '📌' : '📍'}
                      </button>
                      <button
                        onClick={() => handleEditNote(note)}
                        className="edit-button"
                        title="Upravit"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDeleteNote(note.id)}
                        className="delete-button"
                        title="Smazat"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                  <div
                    className="note-content"
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(note.content) }}
                  />
                  <div className="note-footer">
                    <span className="note-date">
                      Vytvořeno: {formatDate(note.created_at)}
                      {note.updated_at !== note.created_at && ` • Upraveno: ${formatDate(note.updated_at)}`}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </div>
  )
}
